// improved-sticky.js - 빠른 스크롤에도 대응하는 Vue 3 Sticky 디렉티브
const STUCK_CLASS = "is-stuck";

export const vSticky = {
  mounted(el, binding) {
    // 기본 옵션 설정
    const defaultOptions = {
      stickyClass: STUCK_CLASS,
      // 스크롤 이벤트 백업 메커니즘 활성화 여부
      useScrollBackup: true,
      // 스크롤 이벤트 스로틀링 시간 (ms)
      scrollThrottle: 100,
    };

    // 사용자 지정 옵션과 기본 옵션 병합
    const options = {
      ...defaultOptions,
      ...(binding.value || {}),
    };

    // position: sticky 적용
    el.style.position = "sticky";

    // top과 z-index는 직접 설정하지 않고 클래스 값을 유지

    // 감시 요소 생성
    const sentinel = document.createElement("div");
    sentinel.classList.add("sticky-sentinel");
    sentinel.style.position = "absolute";
    sentinel.style.top = "0";
    sentinel.style.left = "0";
    sentinel.style.right = "0";
    sentinel.style.height = "1px";
    sentinel.style.visibility = "hidden";
    sentinel.style.pointerEvents = "none"; // 이벤트 방해 방지

    // 부모 요소에 relative 설정 (필요한 경우)
    const parentPosition = window.getComputedStyle(el.parentElement).position;
    if (parentPosition === "static") {
      el.parentElement.style.position = "relative";
    }

    // 감시 요소를 컨테이너 안 가장 위쪽에 삽입
    el.parentElement.insertBefore(sentinel, el.parentElement.firstChild);

    // top 값 계산
    const computedTop = parseInt(window.getComputedStyle(el).top) || 0;

    // sticky 상태 업데이트 함수
    const updateSticky = (isStuck) => {
      if (isStuck) {
        if (!el.classList.contains(options.stickyClass)) {
          el.classList.add(options.stickyClass);
        }
      } else {
        if (el.classList.contains(options.stickyClass)) {
          el.classList.remove(options.stickyClass);
        }
      }
      // 디버그용 데이터 속성
      el.setAttribute("data-stuck", isStuck);
    };

    // Observer 생성
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isStuck = entry.intersectionRatio < 1;
        updateSticky(isStuck);
      },
      {
        threshold: [0, 1],
        rootMargin: `-${computedTop}px 0px 0px 0px`,
      },
    );

    // 스크롤 이벤트 백업 메커니즘
    let scrollHandler = null;
    let isThrottled = false;

    if (options.useScrollBackup) {
      // 스로틀링 함수
      const throttle = (callback, limit) => {
        if (isThrottled) return;
        isThrottled = true;
        setTimeout(() => {
          callback();
          isThrottled = false;
        }, limit);
      };

      // 스크롤 이벤트 핸들러
      scrollHandler = () => {
        throttle(() => {
          // sentinel 요소의 위치 확인
          const sentinelRect = sentinel.getBoundingClientRect();
          // sentinel이 화면 위로 올라갔는지 확인 (top 값 고려)
          const isStuck = sentinelRect.top <= computedTop;
          updateSticky(isStuck);
        }, options.scrollThrottle);
      };

      // 스크롤 이벤트 리스너 등록
      window.addEventListener("scroll", scrollHandler, { passive: true });
    }

    // 감시 시작
    observer.observe(sentinel);

    // 정리를 위해 참조 저장
    el._sticky = {
      options,
      observer,
      sentinel,
      computedTop,
      scrollHandler,
      updateSticky,
    };
  },

  updated(el, binding) {
    // 옵션이 변경된 경우 업데이트
    if (binding.value && JSON.stringify(binding.value) !== JSON.stringify(el._sticky.options)) {
      // 기존 Observer 제거
      el._sticky.observer.disconnect();

      // 스크롤 이벤트 리스너가 있으면 제거
      if (el._sticky.scrollHandler) {
        window.removeEventListener("scroll", el._sticky.scrollHandler);
      }

      // 옵션 업데이트
      el._sticky.options = {
        ...el._sticky.options,
        ...binding.value,
      };

      // top 값 재계산
      const computedTop = parseInt(window.getComputedStyle(el).top) || el._sticky.computedTop || 0;
      el._sticky.computedTop = computedTop;

      // Observer 재설정
      el._sticky.observer = new IntersectionObserver(
        ([entry]) => {
          const isStuck = entry.intersectionRatio < 1;
          el._sticky.updateSticky(isStuck);
        },
        {
          threshold: [0, 1],
          rootMargin: `-${computedTop}px 0px 0px 0px`,
        },
      );

      // 스크롤 이벤트 백업 메커니즘 재설정
      if (el._sticky.options.useScrollBackup) {
        let isThrottled = false;

        // 스로틀링 함수
        const throttle = (callback, limit) => {
          if (isThrottled) return;
          isThrottled = true;
          setTimeout(() => {
            callback();
            isThrottled = false;
          }, limit);
        };

        // 스크롤 이벤트 핸들러
        el._sticky.scrollHandler = () => {
          throttle(() => {
            const sentinelRect = el._sticky.sentinel.getBoundingClientRect();
            const isStuck = sentinelRect.top <= computedTop;
            el._sticky.updateSticky(isStuck);
          }, el._sticky.options.scrollThrottle);
        };

        // 스크롤 이벤트 리스너 등록
        window.addEventListener("scroll", el._sticky.scrollHandler, { passive: true });
      }

      // 감시 재시작
      el._sticky.observer.observe(el._sticky.sentinel);
    }
  },

  unmounted(el) {
    if (el._sticky) {
      if (el._sticky.observer) {
        el._sticky.observer.disconnect();
      }

      // 스크롤 이벤트 리스너 제거
      if (el._sticky.scrollHandler) {
        window.removeEventListener("scroll", el._sticky.scrollHandler);
      }

      // 감시 요소 제거
      if (el._sticky.sentinel && el._sticky.sentinel.parentNode) {
        el._sticky.sentinel.parentNode.removeChild(el._sticky.sentinel);
      }
    }
  },
};

export default {
  install(app) {
    app.directive("sticky", vSticky);
  },
};
